import { redirect } from '@tanstack/react-router';

import { useAuthStore } from '@/lib/auth/auth-store';
import type { Role } from '@/types/user';

export type RouterContext = {
  auth: {
    isAuthenticated: boolean;
    isLoading: boolean;
    hasRole: (role: Role) => boolean;
    hasAnyRole: (roles: Role[]) => boolean;
  };
};

export function getRouterContext(): RouterContext {
  const state = useAuthStore.getState();

  return {
    auth: {
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      hasRole: state.hasRole,
      hasAnyRole: state.hasAnyRole,
    },
  };
}

export function requireAuth(
  context: RouterContext,
  redirectTo: string = '/login'
) {
  if (!context.auth.isAuthenticated) {
    throw redirect({
      to: redirectTo,
      search: {
        redirect: window.location.pathname,
      },
    });
  }
}

export function requireRoles(
  context: RouterContext,
  roles: Role[],
  redirectTo: string = '/'
) {
  requireAuth(context);

  if (!context.auth.hasAnyRole(roles)) {
    throw redirect({ to: redirectTo });
  }
}

export function requireGuest(context: RouterContext, redirectTo?: string) {
  if (context.auth.isAuthenticated) {
    // If no specific redirect provided, redirect based on user role
    if (!redirectTo) {
      const hasDashboardAccess = context.auth.hasAnyRole(['admin', 'agent']);
      redirectTo = hasDashboardAccess ? '/dashboard' : '/';
    }
    throw redirect({ to: redirectTo });
  }
}
